/**
 * The clock. Everything else in the simulation is driven by minutes advancing
 * through here, which is what makes the whole game runnable headless: the
 * client just calls `advance` with a delta, and the balance harness calls it
 * with whole days.
 */
import type { GameState } from '../state/GameState.js';
import { BALANCE } from '../data/balance.js';

export const DAY_NAMES = [
  'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday',
] as const;

export class TimeSystem {
  /**
   * Advance the clock by whole in-game minutes and report how many day
   * boundaries were crossed. Callers use the returned boundaries to run
   * daily/weekly bookkeeping exactly once per boundary, even when a large
   * delta jumps several days at once.
   */
  advance(state: GameState, minutes: number): { daysCrossed: number } {
    if (minutes <= 0) return { daysCrossed: 0 };
    const before = state.time.day;
    state.time.totalMinutes += minutes;
    this.recompute(state);
    return { daysCrossed: state.time.day - before };
  }

  private recompute(state: GameState): void {
    const t = state.time;
    const perDay = BALANCE.time.minutesPerDay;
    t.day = Math.floor(t.totalMinutes / perDay) + 1;
    t.minuteOfDay = t.totalMinutes % perDay;
    t.dayOfWeek = (t.day - 1) % 7;
    t.week = Math.floor((t.day - 1) / 7) + 1;
  }

  hour(state: GameState): number {
    return Math.floor(state.time.minuteOfDay / 60);
  }

  dayName(state: GameState): string {
    return DAY_NAMES[state.time.dayOfWeek] ?? 'Monday';
  }

  formatClock(state: GameState): string {
    const h = Math.floor(state.time.minuteOfDay / 60);
    const m = state.time.minuteOfDay % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  }

  /** Minutes from now until the next occurrence of `hour` on the clock. */
  minutesUntilHour(state: GameState, hour: number): number {
    const target = hour * 60;
    const now = state.time.minuteOfDay;
    return target > now ? target - now : BALANCE.time.minutesPerDay - now + target;
  }

  setSpeed(state: GameState, speed: number): void {
    if (!(BALANCE.time.availableSpeeds as readonly number[]).includes(speed)) return;
    state.time.speed = speed;
    state.time.paused = speed === 0;
  }

  togglePause(state: GameState): void {
    if (state.time.paused) {
      state.time.paused = false;
      if (state.time.speed === 0) state.time.speed = 1;
    } else {
      state.time.paused = true;
    }
  }
}
